// Implements: F-UIX-020, F-UIX-007, F-UIX-010, F-DSG-009, F-DSG-010
/**
 * "Activo en" inside the editor (mockup v3 P5): one switch per scope the person manages, for the
 * open profile, in the listing's order, each saying what that scope uses now.
 *
 * Which scopes the profile can serve is the server's (`activatable_in`, rule 1.3: a profile is
 * active only where every reader of the scope can read it); the client never derives it. A managed
 * scope outside that list keeps its switch, disabled, with the reason. A scope the person cannot
 * manage (`may_edit` false, P6-OD-16) is listed as text, and only while the profile is active there.
 * ON sends `PUT .../active-profile` for that scope only (the other scopes keep their marks), OFF the
 * `DELETE`; no confirmation, the help under the switches says what it does and the switch undoes it.
 *
 * Each switch is its own container, keyed by its scope (checklist 14), with one request at a time:
 * it is disabled while its request runs, and every request is sent once (`retry: false`,
 * checklist 13). While it runs the switch shows the state asked for; a refusal puts it back and
 * says why, inline; a success keeps it until the listing, read again, agrees.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { useTranslation } from "../../../shared/i18n";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import type { ScopeStateView } from "../../../types/design";
import { ActiveInGroup, ActiveInReadOnlyLine, ActiveInSwitchLine } from "../components/ActiveInField";
import { useScopeActivation } from "../hooks/useDesignProfiles";
import { scopeKey } from "../model/scopeModel";

/** One scope of the listing, as "Activo en" shows it for the open profile. */
export interface ActiveInScope {
  /** The scope as the listing states it now. */
  readonly state: ScopeStateView;
  /** Its name for the person ("Personal", "Equipo Producto"). */
  readonly label: string;
  /** The server lists it in the profile's `activatable_in`. */
  readonly allowed: boolean;
  /** The readable name of another profile active there now, if any. */
  readonly otherName: string | null;
}

export interface ActiveInContainerProps {
  readonly profileId: string;
  readonly scopes: readonly ActiveInScope[];
}

function ScopeSwitch({ scope, profileId }: { readonly scope: ActiveInScope; readonly profileId: string }): ReactElement {
  const { t } = useTranslation();
  const activation = useScopeActivation();
  const [pending, setPending] = useState(false);
  const [requested, setRequested] = useState<boolean | null>(null);
  const [refusal, setRefusal] = useState<ApiError | null>(null);

  const actual = scope.state.active_profile_id === profileId;
  // The listing now says what was asked for: nothing is left to show on its behalf.
  if (requested !== null && !pending && requested === actual) setRequested(null);

  const change = async (next: boolean): Promise<void> => {
    setPending(true);
    setRequested(next);
    setRefusal(null);
    const outcome = next ? await activation.activate(scope.state, profileId) : await activation.clear(scope.state);
    setPending(false);
    if (!outcome.ok) {
      setRequested(null);
      setRefusal(outcome.error);
    }
  };
  const meta =
    scope.otherName !== null
      ? t("design.activeIn.now", { name: scope.otherName })
      : scope.state.active_profile_id === null
        ? t("design.activeIn.system")
        : null;

  return (
    <ActiveInSwitchLine
      label={scope.label}
      checked={requested ?? actual}
      pending={pending}
      allowed={scope.allowed}
      meta={meta}
      error={refusal === null ? null : userMessage(refusal, t("design.active.failed"))}
      onChange={(next) => {
        void change(next);
      }}
    />
  );
}

export function ActiveInContainer({ profileId, scopes }: ActiveInContainerProps): ReactElement | null {
  const shown = scopes.filter((scope) => scope.state.may_edit || scope.state.active_profile_id === profileId);
  if (shown.length === 0) return null;
  return (
    <ActiveInGroup>
      {shown.map((scope) =>
        scope.state.may_edit ? (
          <ScopeSwitch key={scopeKey(scope.state)} scope={scope} profileId={profileId} />
        ) : (
          <ActiveInReadOnlyLine key={scopeKey(scope.state)} label={scope.label} />
        ),
      )}
    </ActiveInGroup>
  );
}
